<template>
  <b-row>
    <b-col v-if="!loading" cols="12" lg="12">
      <b-row>
        <b-col md="9">
          <info-card
            :ombudsman="record"
            :classification-maps="classificationMaps"
            :classification-groups="classificationGroups"
            @download-file="downloadFile"
          />
          <processing-card
            :loading-events="loadingEvents"
            :events="events"
            :can-edit="canEdit"
            :classification-maps="classificationMaps"
            :company-users="companyUsers"
            :managed-by-company-users="managedByCompanyUsers"
            :conformita-users="conformitaUsers"
            @add-event="$refs.eventModal.show()"
            @resolve-pendency="resolvePendency"
            @resolve-task="resolveTask"
            @resend-task="resendTask"
            @show-event="showEvent"
          />
        </b-col>
        <b-col md="3">
          <monitoring-card
            :ombudsman="record"
            :can-edit="canEdit"
            :loading-monitorings="loadingMonitorings"
            :monitorings="monitorings"
            :company-users="companyUsers"
            :managed-by-company-users="managedByCompanyUsers"
            :conformita-users="conformitaUsers"
            @open="$refs.monitoringModal.show()"
            @toggle-response="toggleMonitoringResponse(record)"
            @toggle-visibility="toggleMonitoringVisibility"
          />
          <classification-card
            :ombudsman="record"
            :can-edit="canEdit"
            :classification-groups="classificationGroups"
            @open="$refs.classificationModal.show()"
          />
          <phone-card
            :ombudsman="record"
            :can-edit="canEdit"
            :phone-service-protocol.sync="phoneServiceProtocol"
            :phone-service-receive-date.sync="phoneServiceReceiveDate"
            :phone-service-receive-time.sync="phoneServiceReceiveTime"
            :old-phone-service-protocol="oldPhoneServiceProtocol"
            @associate="onAssociatePhoneServiceProtocol"
            @cancel-replace="cancelPhoneServiceProtocolReplace"
            @replace="replacePhoneServiceProtocol"
          />
        </b-col>
      </b-row>

      <ombudsman-event-modal
        ref="eventModal"
        :record="record"
        :pendency="pendencyEvent"
        :task="taskEvent"
        @new-event="onNewEvent"
        @status-changed="onStatusChanged"
        @cancel="onEventModalCancel"
      />
      <ombudsman-monitoring-modal
        ref="monitoringModal"
        :record="record"
        @new-monitoring="loadMonitoring"
      />
      <classification-modal
        ref="classificationModal"
        :ombudsman="record"
        @classification-changed="onClassificationChanged"
      />
    </b-col>
  </b-row>
</template>

<script>
import {
  getOmbudsman,
  getOmbudsmanEvents,
  getOmbudsmanMonitoring,
  setOmbudsmanMonitoringVisibility,
  setOmbudsmanMonitoringResponseEnabled,
  associatePhoneServiceProtocol,
  resendOmbudsmanTask,
} from "@/api/ombudsman";
import { getSystemUsers } from "@/api/user";
import { getCompanyUsers } from "@/api/company";
import { mapGetters } from "vuex";
import moment from "moment/moment";
import { getActiveCompanyClassificationGroupsOptions } from "../../../api/company";

// Componentes modulares
import InfoCard from "./InfoCard.vue";
import ProcessingCard from "./ProcessingCard.vue";
import MonitoringCard from "./MonitoringCard.vue";
import PhoneCard from "./PhoneCard.vue";
import ClassificationCard from "./ClassificationCard.vue";

// Modais existentes
import OmbudsmanEventModal from "../ombudsman-event-modal";
import OmbudsmanMonitoringModal from "../ombudsman-monitoring-modal";
import ClassificationModal from "@/views/complaints/classification-modal";

export default {
  name: "OmbudsmanView",
  components: {
    InfoCard,
    ProcessingCard,
    MonitoringCard,
    PhoneCard,
    ClassificationCard,
    OmbudsmanEventModal,
    OmbudsmanMonitoringModal,
    ClassificationModal,
  },
  props: {
    id: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      record: {},
      companyUsers: {},
      managedByCompanyUsers: {},
      conformitaUsers: {},
      loadingEvents: false,
      events: [],
      phoneServiceProtocol: null,
      phoneServiceReceiveDate: null,
      phoneServiceReceiveTime: null,
      oldPhoneServiceProtocol: null,
      oldPhoneServiceReceiveDate: null,
      oldPhoneServiceReceiveTime: null,
      loadingMonitorings: false,
      monitorings: [],
      pendencyEvent: null,
      taskEvent: null,
      classificationGroups: [],
    };
  },
  computed: {
    ...mapGetters(["profile", "classificationMaps", "permissions"]),
    masterUser() {
      const role = this.profile.role;
      return role === "root" || role === "company-root";
    },
    conformitaUser() {
      return !!this.conformitaUsers[this.profile._id];
    },
    canEdit() {
      if (parseInt(this.profile.role) === this.profile.role) {
        return (
          (this.permissions.ombudsman && this.permissions.ombudsman.edit) ||
          this.permissions.ombudsman.create
        );
      }
      return true;
    },
  },
  async created() {
    this.loading = true;

    try {
      // Carregar dados da ouvidoria
      const response = await getOmbudsman(this.id);
      this.record = response.data;

      // Carregar grupos de classificação
      const groups = await getActiveCompanyClassificationGroupsOptions(
        this.record.company._id,
        "ombudsman"
      );
      this.classificationGroups = groups.data;

      // Carregar usuários da empresa
      let resp = await getCompanyUsers(this.record.company._id, true);
      this.companyUsers = resp.data.reduce((obj, user) => {
        obj[user["_id"]] = user;
        return obj;
      }, {});

      // Carregar usuários da empresa gerenciadora
      if (this.record.company.managedBy) {
        let managedCompanyUsers = (
          await getCompanyUsers(this.record.company.managedBy, true)
        ).data;
        this.managedByCompanyUsers = managedCompanyUsers.reduce((obj, user) => {
          obj[user["_id"]] = user;
          return obj;
        }, {});
      }

      // Carregar usuários do sistema
      resp = await getSystemUsers();
      this.conformitaUsers = resp.data.reduce((obj, user) => {
        obj[user["_id"]] = user;
        return obj;
      }, {});

      // Carregar dados adicionais
      this.loadMonitoring();
      this.loadClassifications();
      await this.loadEvents();

      // Verificar se há evento específico para mostrar
      const { event } = this.$route.query;
      if (event) {
        let query = Object.assign({}, this.$route.query);
        delete query.event;
        this.$router.replace({ query });
        const showQueryEvent = () => {
          const element = document.getElementById(`event-${event}`);
          if (!element) {
            this.$nextTick(showQueryEvent);
          }
          this.showEvent(event);
        };
        this.$nextTick(showQueryEvent);
      }
    } catch (error) {
      console.error("Erro ao carregar dados da ouvidoria:", error);
      this.$bvToast.toast("Erro ao carregar dados da ouvidoria", {
        title: "Erro",
        variant: "danger",
        solid: true,
      });
    } finally {
      this.loading = false;
    }
  },
  methods: {
    moment,

    showEvent(id) {
      const element = document.getElementById(`event-${id}`);
      if (element) {
        const offset = 55;
        const bodyRect = document.body.getBoundingClientRect().top;
        const elementRect = element.getBoundingClientRect().top;
        const elementPosition = elementRect - bodyRect;
        const offsetPosition = elementPosition - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      }
    },

    resolvePendency(event) {
      this.pendencyEvent = event;
      this.$refs.eventModal.show();
    },

    resolveTask(event) {
      this.taskEvent = event;
      this.$refs.eventModal.show();
    },

    async resendTask(event) {
      try {
        await resendOmbudsmanTask(this.record._id, event._id);
        this.$bvToast.toast("E-mail reenviado com sucesso!", {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
          noCloseButton: false,
        });
      } catch (error) {
        if (
          error.response &&
          (error.response.status === 403 || error.response.status === 404)
        ) {
          this.$bvToast.toast(error.response.data, {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          });
        } else {
          this.$bvToast.toast(
            error.response?.statusText || "Falha ao reenviar e-mail",
            {
              title: "Erro",
              toastClass: "custom-toast custom-toast-error",
              variant: "light",
              solid: true,
              autoHideDelay: 10000,
              noCloseButton: false,
            }
          );
        }
      }
    },

    onEventModalCancel() {
      this.pendencyEvent = null;
      this.taskEvent = null;
    },

    async onNewEvent() {
      await this.loadEvents();
      await this.loadMonitoring();
    },

    async loadEvents() {
      this.pendencyEvent = null;
      this.taskEvent = null;
      this.loadingEvents = true;

      try {
        const response = await getOmbudsmanEvents(this.record._id);
        this.events = response.data;
      } catch (error) {
        console.error("Erro ao carregar eventos:", error);
        this.$bvToast.toast("Erro ao carregar eventos", {
          title: "Erro",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.loadingEvents = false;
      }
    },

    async loadMonitoring() {
      this.loadingMonitorings = true;

      try {
        const response = await getOmbudsmanMonitoring(this.record._id);
        this.monitorings = response.data;
      } catch (error) {
        console.error("Erro ao carregar acompanhamentos:", error);
        this.$bvToast.toast("Erro ao carregar acompanhamentos", {
          title: "Erro",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.loadingMonitorings = false;
      }
    },

    downloadFile(file) {
      const link = `${window.location.origin}/api/ombudsman/${this.record._id}/files/${file._id}`;
      const a = document.createElement("a");
      a.setAttribute("download", "");
      a.setAttribute("href", link);
      a.setAttribute("target", "_blank");
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },

    onClassificationChanged(classification) {
      this.$set(this.record, "classification", classification);
    },

    onStatusChanged(status) {
      this.record.status = status;
    },

    async toggleMonitoringVisibility(monitoring) {
      try {
        await setOmbudsmanMonitoringVisibility(monitoring, !monitoring.hidden);
        monitoring.hidden = !monitoring.hidden;
        this.$bvToast.toast(
          `Acompanhamento ${
            monitoring.hidden ? "ocultado" : "exibido"
          } com sucesso!`,
          {
            title: "Sucesso",
            variant: "success",
            solid: true,
          }
        );
        this.$forceUpdate();
      } catch (error) {
        console.error("Erro ao alterar visibilidade:", error);
        this.$bvToast.toast("Erro ao alterar visibilidade do acompanhamento", {
          title: "Erro",
          variant: "danger",
          solid: true,
        });
      }
    },

    async toggleMonitoringResponse(record) {
      try {
        await setOmbudsmanMonitoringResponseEnabled(
          record,
          !record.monitoringResponseEnabled
        );
        record.monitoringResponseEnabled = !record.monitoringResponseEnabled;
        this.$bvToast.toast(
          `Resposta ${
            record.monitoringResponseEnabled ? "habilitada" : "desabilitada"
          } com sucesso!`,
          {
            title: "Sucesso",
            variant: "success",
            solid: true,
          }
        );
        await this.loadEvents();
        this.$forceUpdate();
      } catch (error) {
        console.error("Erro ao alterar resposta:", error);
        this.$bvToast.toast("Erro ao alterar resposta do acompanhamento", {
          title: "Erro",
          variant: "danger",
          solid: true,
        });
      }
    },

    async loadClassifications() {
      this.$store.dispatch(
        "RefreshCompanyClassifications",
        this.record.company._id
      );
    },

    replacePhoneServiceProtocol() {
      this.phoneServiceProtocol = this.record.phoneServiceProtocol;
      this.oldPhoneServiceProtocol = this.record.phoneServiceProtocol;
      const date = moment(this.record.phoneServiceReceiveDate);
      this.phoneServiceReceiveDate = this.oldPhoneServiceReceiveDate =
        date.format("YYYY-MM-DD");
      this.phoneServiceReceiveTime = this.oldPhoneServiceReceiveTime =
        date.format("HH:mm");
      this.record.phoneServiceProtocol = null;
      this.record.phoneServiceReceiveDate = null;
    },

    cancelPhoneServiceProtocolReplace() {
      this.record.phoneServiceProtocol = this.oldPhoneServiceProtocol;
      let date = this.oldPhoneServiceReceiveDate;
      if (this.oldPhoneServiceReceiveTime) {
        date = moment(
          `${this.oldPhoneServiceReceiveDate} ${this.oldPhoneServiceReceiveTime}`
        ).format();
      }
      this.record.phoneServiceReceiveDate = date;
      this.oldPhoneServiceProtocol = null;
      this.oldPhoneServiceReceiveDate = null;
      this.oldPhoneServiceReceiveTime = null;
      this.phoneServiceProtocol = null;
    },

    async onAssociatePhoneServiceProtocol() {
      if (this.phoneServiceProtocol) {
        try {
          let date = this.phoneServiceReceiveDate;
          if (this.phoneServiceReceiveTime) {
            date = moment(
              `${this.phoneServiceReceiveDate} ${this.phoneServiceReceiveTime}`
            ).format();
          }
          await associatePhoneServiceProtocol(
            this.record._id,
            this.phoneServiceProtocol,
            date
          );
          this.record.phoneServiceProtocol = this.phoneServiceProtocol;
          this.record.phoneServiceReceiveDate = date;
          this.$bvToast.toast("Protocolo telefônico associado com sucesso!", {
            title: "Sucesso",
            variant: "success",
            solid: true,
          });
          this.$forceUpdate();
        } catch (error) {
          console.error("Erro ao associar protocolo:", error);
          this.$bvToast.toast("Erro ao associar protocolo telefônico", {
            title: "Erro",
            variant: "danger",
            solid: true,
          });
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
// Estilos específicos do componente principal
.ombudsman-view {
  .row {
    margin-bottom: 1.5rem;
  }

  .col-md-3 {
    .phone-card,
    .classification-card,
    .monitoring-card {
      margin-bottom: 1.5rem;
    }
  }
}

// Loading state
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
</style>
